import { TAG_COLORS } from '../../shared/icons.generated';
import { RSVP_LABEL, type Opportunity, type RsvpType } from '../../shared/opportunity';
import { GOALS } from '../../shared/taxonomy';

/**
 * The layers icon in the My Schedule header color-codes the week.
 *
 * The original spec suggested coloring by "paid" and by "requires approval".
 * Only 27 of 997 events carry any price signal, so a paid dimension would
 * color almost nothing. What follows keeps the useful half of that idea —
 * "what still needs action from me" — and adds two lenses the data supports
 * well, one of which (neighborhood) is the feasibility view the research
 * says nobody currently offers.
 */

export type ColorKey = 'none' | 'intent' | 'rsvp' | 'area' | 'topic';

export const COLOR_OPTIONS: { id: ColorKey; label: string; hint: string }[] = [
  { id: 'none', label: 'No color', hint: 'Plain parchment blocks' },
  { id: 'intent', label: 'Event type', hint: 'What you wanted to do' },
  { id: 'rsvp', label: 'RSVP status', hint: 'What still needs action' },
  { id: 'area', label: 'Neighborhood', hint: 'How far you are traveling' },
  { id: 'topic', label: 'Topic', hint: 'Your first tag on each event' },
];

/** Keyed by goal key. */
const GOAL_COLORS: Record<string, string> = {
  'grow-my-network': '#b9dcff',
  'attend-the-conference': '#fff0b9',
  'do-something-interactive': '#d0b9ff',
  socialize: '#ffb9f1',
  'get-outside': '#bfffb9',
  'watch-a-film-or-show': '#ffcbb9',
};

const RSVP_COLORS: Partial<Record<RsvpType, string>> = {
  open_to_public: '#bfffb9',
  apply_to_attend: '#fff0b9',
  invite_only: '#ffb9b9',
  paid: '#ffcbb9',
  see_listing: '#b5b0ab',
};

const AREA_COLORS: Record<string, string> = {
  Manhattan: '#b9dcff',
  Brooklyn: '#bfffb9',
  Queens: '#fff0b9',
  Bronx: '#ffcbb9',
  'Staten Island': '#ffb9f1',
  'Governors Island': '#b9fff8',
  'Roosevelt Island': '#d0b9ff',
  'New Jersey': '#ffe0b9',
};

const goalLabel = (key: string) => GOALS.find((g) => g.key === key)?.label ?? key;

export function colorFor(event: Opportunity, key: ColorKey): string | null {
  switch (key) {
    case 'none':
      return null;
    case 'intent':
      return event.goals.length ? (GOAL_COLORS[event.goals[0].key] ?? null) : null;
    case 'rsvp':
      return RSVP_COLORS[event.rsvpType ?? 'see_listing'] ?? null;
    case 'area': {
      if (!event.neighborhood) return null;
      const borough = event.neighborhood.split(' - ')[0];
      return AREA_COLORS[borough] ?? null;
    }
    case 'topic':
      return event.topics.length ? (TAG_COLORS[`topic:${event.topics[0].key}`] ?? null) : null;
    default:
      return null;
  }
}

/** Legend entries for the currently selected lens. */
export function legendFor(key: ColorKey): { label: string; color: string }[] {
  switch (key) {
    case 'intent':
      return Object.entries(GOAL_COLORS).map(([goal, color]) => ({ label: goalLabel(goal), color }));
    case 'rsvp':
      return Object.entries(RSVP_COLORS).map(([rsvp, color]) => ({
        label: RSVP_LABEL[rsvp as RsvpType],
        color: color as string,
      }));
    case 'area':
      return Object.entries(AREA_COLORS).map(([label, color]) => ({ label, color }));
    default:
      return [];
  }
}
